import React, { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Table, Tag, Spin, Typography } from "antd";
import {
    UserOutlined, BookOutlined, ReadOutlined, FileTextOutlined,
    PhoneOutlined, RiseOutlined, EyeOutlined, DollarOutlined
} from "@ant-design/icons";
import { Link } from "react-router-dom";
import axios from '../../../utils/axios';
import ZipImporter from "../../../component/ZipImporter";

const { Title, Text } = Typography;

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = async () => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_URL}/dashboard/stats`
            );
            setStats(result.data);
        } catch (e) {
            console.error("Failed to fetch stats:", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStats();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Spin size="large" tip="Đang tải dữ liệu..." />
            </div>
        );
    }

    const statCards = [
        {
            title: "Tổng Topic",
            value: stats?.counts?.topics || 0,
            icon: <BookOutlined />,
            color: "#1890ff",
            bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            link: "/admin/topic",
        },
        {
            title: "Tổng Course",
            value: stats?.counts?.courses || 0,
            icon: <ReadOutlined />,
            color: "#52c41a",
            bg: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            link: "/admin/course",
        },
        {
            title: "Tổng Lesson",
            value: stats?.counts?.lessons || 0,
            icon: <FileTextOutlined />,
            color: "#faad14",
            bg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            link: "/admin/lesson",
        },
        {
            title: "Tổng người dùng",
            value: stats?.counts?.users || 0,
            icon: <UserOutlined />,
            color: "#722ed1",
            bg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            link: "/admin/user",
        },
        {
            title: "Liên hệ",
            value: stats?.counts?.contacts || 0,
            icon: <PhoneOutlined />,
            color: "#eb2f96",
            bg: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
            link: "/admin/contact",
        },
        {
            title: "Bài viết",
            value: stats?.counts?.blogs || 0,
            icon: <EyeOutlined />,
            color: "#13c2c2",
            bg: "linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)",
            link: "/admin/blog",
        },
    ];

    const recentLessonColumns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 60,
            render: (id) => <Tag color="blue">#{id}</Tag>,
        },
        {
            title: "Tiêu đề",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
            render: (title, record) => (
                <Link to={`/admin/lesson/${record.slug}`} className="text-blue-600 hover:text-blue-800 font-medium">
                    {title}
                </Link>
            ),
        },
        {
            title: "Ngày tạo",
            dataIndex: "create_at",
            key: "create_at",
            width: 170,
            render: (date) => date ? new Date(date).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—",
        },
    ];

    const recentUserColumns = [
        {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 60,
            render: (id) => <Tag color="purple">#{id}</Tag>,
        },
        {
            title: "Tên",
            dataIndex: "username",
            key: "username",
            render: (name) => <span className="font-medium">{name || "—"}</span>,
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
            ellipsis: true,
        },
    ];

    const lessonsPerCourseColumns = [
        {
            title: "Course",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
            render: (title) => <span className="font-medium">{title}</span>,
        },
        {
            title: "Số Lesson",
            dataIndex: "lesson_count",
            key: "lesson_count",
            width: 100,
            render: (count) => <Tag color="green" className="font-bold text-base px-3">{count}</Tag>,
        },
    ];

    return (
        <div>
            {/* Page Header */}
            <div className="mb-6">
                <Title level={3} className="!mb-1 !text-gray-800">📊 Dashboard</Title>
                <Text type="secondary">Tổng quan hệ thống Effortless English</Text>
            </div>

            {/* Stats Cards */}
            <Row gutter={[16, 16]} className="mb-6">
                {statCards.map((card, index) => (
                    <Col xs={12} sm={8} lg={4} key={index}>
                        <Link to={card.link}>
                            <Card
                                hoverable
                                className="rounded-xl border-0 overflow-hidden"
                                style={{ background: card.bg }}
                                bodyStyle={{ padding: "20px" }}
                            >
                                <div className="text-white">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-3xl opacity-80">{card.icon}</span>
                                        <RiseOutlined className="text-lg opacity-60" />
                                    </div>
                                    <div className="text-3xl font-bold">{card.value}</div>
                                    <div className="text-sm opacity-90 mt-1">{card.title}</div>
                                </div>
                            </Card>
                        </Link>
                    </Col>
                ))}
            </Row>

            {/* Bulk ZIP import */}
            <div className="mb-6">
                <ZipImporter />
            </div>

            {/* Content Rows */}
            <Row gutter={[16, 16]}>
                {/* Recent Lessons */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<span className="font-semibold">📝 Bài học gần đây</span>}
                        className="rounded-xl shadow-sm border-0 h-full"
                        headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        extra={<Link to="/admin/lesson" className="text-blue-500">Xem tất cả →</Link>}
                    >
                        <Table
                            dataSource={stats?.recentLessons || []}
                            columns={recentLessonColumns}
                            pagination={false}
                            rowKey="id"
                            size="small"
                        />
                    </Card>
                </Col>

                {/* Lessons Per Course */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<span className="font-semibold">📚 Số bài học theo Course</span>}
                        className="rounded-xl shadow-sm border-0 h-full"
                        headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        extra={<Link to="/admin/course" className="text-blue-500">Xem tất cả →</Link>}
                    >
                        <Table
                            dataSource={stats?.lessonsPerCourse || []}
                            columns={lessonsPerCourseColumns}
                            pagination={false}
                            rowKey="title"
                            size="small"
                        />
                    </Card>
                </Col>

                {/* Recent Users */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<span className="font-semibold">👤 Người dùng gần đây</span>}
                        className="rounded-xl shadow-sm border-0"
                        headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                        extra={<Link to="/admin/user" className="text-blue-500">Xem tất cả →</Link>}
                    >
                        <Table
                            dataSource={stats?.recentUsers || []}
                            columns={recentUserColumns}
                            pagination={false}
                            rowKey="id"
                            size="small"
                        />
                    </Card>
                </Col>

                {/* Quick Actions */}
                <Col xs={24} lg={12}>
                    <Card
                        title={<span className="font-semibold">⚡ Thao tác nhanh</span>}
                        className="rounded-xl shadow-sm border-0"
                        headStyle={{ borderBottom: '2px solid #f0f0f0' }}
                    >
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { label: "Tạo Topic mới", path: "/admin/topic/create", icon: "📁", color: "bg-blue-50 hover:bg-blue-100 border-blue-200" },
                                { label: "Tạo Course mới", path: "/admin/course/create", icon: "📚", color: "bg-green-50 hover:bg-green-100 border-green-200" },
                                { label: "Tạo Lesson mới", path: "/admin/lesson/create", icon: "📝", color: "bg-orange-50 hover:bg-orange-100 border-orange-200" },
                                { label: "Tạo bài viết", path: "/admin/blog/create", icon: "✍️", color: "bg-purple-50 hover:bg-purple-100 border-purple-200" },
                                { label: "Quản lý người dùng", path: "/admin/user", icon: "👥", color: "bg-cyan-50 hover:bg-cyan-100 border-cyan-200" },
                                { label: "Xem liên hệ", path: "/admin/contact", icon: "📞", color: "bg-pink-50 hover:bg-pink-100 border-pink-200" },
                            ].map((action, i) => (
                                <Link to={action.path} key={i}>
                                    <div className={`${action.color} border rounded-lg p-4 transition-all cursor-pointer flex items-center gap-3`}>
                                        <span className="text-2xl">{action.icon}</span>
                                        <span className="font-medium text-gray-700 text-sm">{action.label}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
